import { useState, useEffect } from 'react';
import { api } from '../api/client';

// ─── Types ────────────────────────────────────────────────────────────────────

type ChangeStatus = 'pending' | 'approved' | 'documented' | 'completed' | 'overdue' | 'rejected' | 'failed';
type RequestType = 'reserve_change' | 'status_change' | 'data_update' | 'party_update';

interface ChangeEntry {
  field: string;
  field_label: string;
  old_value: string;
  new_value: string;
  display_format?: string;
}

interface ChangeRequest {
  id: string;
  claim_id: string;
  claim_number: string;
  request_type: RequestType;
  changes: ChangeEntry[];
  reason: string;
  priority: 'normal' | 'urgent';
  status: ChangeStatus;
  handler_name: string;
  submitted_at: string;
  reviewed_at: string | null;
  reviewer_name: string | null;
  reviewer_note: string | null;
  documented_at: string | null;
  verified_at: string | null;
  verified_by: string | null;
  fh_diary_id: string | null;
  fh_note_id: string | null;
  stubbed: boolean;
  error_message: string | null;
}

interface Props {
  claimId: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatValue(entry: ChangeEntry): string {
  if (entry.display_format === 'currency') {
    const num = parseFloat(entry.new_value);
    if (!isNaN(num)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(num);
    }
  }
  return entry.new_value || '—';
}

function formatOldValue(entry: ChangeEntry): string {
  if (entry.display_format === 'currency') {
    const num = parseFloat(entry.old_value);
    if (!isNaN(num)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(num);
    }
  }
  return entry.old_value || '—';
}

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  reserve_change: 'Reserve Change',
  status_change: 'Status Change',
  data_update: 'Data Update',
  party_update: 'Party Update',
};

// ─── Status Badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approved', className: 'bg-blue-100 text-blue-800' },
  documented: { label: 'Documented', className: 'bg-green-100 text-green-800' },
  completed: { label: 'Verified', className: 'bg-emerald-100 text-emerald-800' },
  overdue: { label: 'Overdue', className: 'bg-red-100 text-red-800' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-800' },
};

function StatusBadge({ status, stubbed }: { status: ChangeStatus; stubbed?: boolean }) {
  if (status === 'documented' && stubbed) {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800">
        Pending FH API
      </span>
    );
  }
  const config = STATUS_CONFIG[status] ?? { label: status, className: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

function TypeBadge({ type }: { type: RequestType }) {
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700">
      {REQUEST_TYPE_LABELS[type] ?? type}
    </span>
  );
}

// ─── Change Item ──────────────────────────────────────────────────────────────

function ChangeItem({ request }: { request: ChangeRequest }) {
  const [expanded, setExpanded] = useState(false);

  const summary = request.changes
    .map((c) => `${c.field_label}: ${formatOldValue(c)} → ${formatValue(c)}`)
    .join(', ');

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header row — always visible */}
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full text-left px-4 py-3 bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex flex-wrap items-center gap-3">
          {/* Timeline dot + date */}
          <div className="flex items-center gap-2 min-w-[130px]">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
              request.status === 'completed'
                ? 'bg-emerald-500'
                : request.status === 'documented'
                ? 'bg-green-500'
                : request.status === 'overdue'
                ? 'bg-red-500'
                : request.status === 'pending'
                ? 'bg-amber-400'
                : request.status === 'approved'
                ? 'bg-blue-500'
                : 'bg-red-400'
            }`} />
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {formatDate(request.submitted_at)}
            </span>
          </div>

          {/* Handler */}
          <span className="text-sm font-medium text-gray-800 min-w-[120px]">
            {request.handler_name || '—'}
          </span>

          {/* Badges */}
          <TypeBadge type={request.request_type} />
          <StatusBadge status={request.status} stubbed={request.stubbed} />

          {request.priority === 'urgent' && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700">
              Urgent
            </span>
          )}

          {/* Summary */}
          <span className="text-xs text-gray-500 flex-1 text-left truncate hidden sm:block">
            {summary}
          </span>

          {/* Chevron */}
          <svg
            className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Mobile summary line */}
        <p className="text-xs text-gray-500 mt-1 text-left sm:hidden truncate">{summary}</p>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-4 space-y-4">

          {/* Changes table */}
          <div>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">Changes</p>
            <div className="space-y-1.5">
              {request.changes.map((entry, i) => (
                <div key={i} className="flex flex-wrap items-baseline gap-2 text-sm">
                  <span className="font-medium text-gray-700 min-w-[120px]">{entry.field_label}</span>
                  <span className="text-gray-400 line-through text-xs">{formatOldValue(entry)}</span>
                  <span className="text-gray-400">→</span>
                  <span className="font-semibold text-gray-900">{formatValue(entry)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Reason */}
          <div>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Reason</p>
            <p className="text-sm text-gray-700">{request.reason}</p>
          </div>

          {/* Authorization info */}
          {(request.reviewer_name || request.reviewer_note) && (
            <div className="flex items-start gap-2.5 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
              <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <div>
                {request.reviewer_name && (
                  <p className="text-sm text-gray-800">
                    {request.status === 'rejected' ? 'Rejected' : 'Authorized'} by{' '}
                    <span className="font-bold text-gray-900">{request.reviewer_name}</span>
                    {request.reviewed_at && (
                      <span className="text-gray-500"> on {formatDateTime(request.reviewed_at)}</span>
                    )}
                  </p>
                )}
                {request.reviewer_note && (
                  <p className="text-sm text-gray-600 mt-0.5 italic">"{request.reviewer_note}"</p>
                )}
              </div>
            </div>
          )}

          {/* Error message */}
          {request.error_message && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <p className="text-xs text-red-700">{request.error_message}</p>
            </div>
          )}

          {/* Timestamps */}
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-400">
            <span>Submitted: {formatDateTime(request.submitted_at)}</span>
            {request.documented_at && <span>Documented: {formatDateTime(request.documented_at)}</span>}
            {request.verified_at && <span className="text-emerald-600">Verified: {formatDateTime(request.verified_at)}</span>}
          </div>

          {/* Verified badge */}
          {request.status === 'completed' && (
            <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <svg className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-emerald-700">
                Change verified in FileHandler{request.verified_by === 'system-auto' ? ' (auto-detected)' : ''}.
              </p>
            </div>
          )}

          {/* Overdue warning */}
          {request.status === 'overdue' && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-red-700">
                Change has not been applied in FileHandler after 24+ hours.
              </p>
            </div>
          )}

          {/* FH API tracking */}
          {(request.status === 'documented' || request.status === 'completed' || request.status === 'overdue') && (
            <div className="text-xs text-gray-500 space-y-0.5">
              {request.stubbed ? (
                <p className="text-amber-600">Pending API access — diary/note will be created when FH API is enabled.</p>
              ) : (
                <>
                  {request.fh_diary_id && <p>Diary ID: <span className="font-mono">{request.fh_diary_id}</span></p>}
                  {request.fh_note_id && <p>Note ID: <span className="font-mono">{request.fh_note_id}</span></p>}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ChangeHistory({ claimId }: Props) {
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!claimId) return;
    setLoading(true);
    setError(null);
    (api as any)
      .getChanges({ claim_id: claimId })
      .then((res: any) => {
        const items: ChangeRequest[] = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : [];
        setRequests(items);
      })
      .catch((err: any) => {
        setError(err.message || 'Failed to load change history.');
      })
      .finally(() => setLoading(false));
  }, [claimId]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
        Change Request History
      </h3>

      {loading && (
        <div className="flex items-center gap-2 py-8 justify-center">
          <svg className="animate-spin w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <span className="text-sm text-gray-500">Loading history...</span>
        </div>
      )}

      {!loading && error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!loading && !error && requests.length === 0 && (
        <p className="text-sm text-gray-500 py-6 text-center">No change requests for this claim.</p>
      )}

      {!loading && !error && requests.length > 0 && (
        <div className="space-y-2">
          {requests.map((req) => (
            <ChangeItem key={req.id} request={req} />
          ))}
        </div>
      )}
    </div>
  );
}
